import { API_BASE_URL } from '../../../config';
import type {
  SearchQuery,
  SearchResponse,
  AutocompleteQuery,
  AutocompleteResponse,
} from '../types';

const API_BASE = `${API_BASE_URL}/api/v1/search`;

async function handleResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Request failed' }));
    throw new Error(error.message || `HTTP ${response.status}`);
  }
  return response.json() as Promise<T>;
}

function getAuthHeaders(): HeadersInit {
  const token = localStorage.getItem('accessToken');
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

function buildQueryString(params: Record<string, unknown>): string {
  const searchParams = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== '') {
      searchParams.set(key, String(value));
    }
  }
  return searchParams.toString();
}

/**
 * Perform a full-text search with optional filters and pagination.
 * GET /api/v1/search?q=...&type=...&category=...&country=...&dateFrom=...&dateTo=...&sortBy=...&page=...&pageSize=...
 */
export async function search(query: SearchQuery): Promise<SearchResponse> {
  const qs = buildQueryString(query as Record<string, unknown>);
  const response = await fetch(`${API_BASE}?${qs}`, {
    headers: getAuthHeaders(),
  });
  return handleResponse<SearchResponse>(response);
}

/**
 * Fetch autocomplete suggestions for partial text input.
 * GET /api/v1/search/autocomplete?q=...&type=...
 */
export async function autocomplete(query: AutocompleteQuery): Promise<AutocompleteResponse> {
  const qs = buildQueryString(query as Record<string, unknown>);
  const response = await fetch(`${API_BASE}/autocomplete?${qs}`, {
    headers: getAuthHeaders(),
  });
  return handleResponse<AutocompleteResponse>(response);
}

export const searchApi = {
  search,
  autocomplete,
};

export default searchApi;
